import { IconName } from '@optimus-web/icons';
import { DropDownButton, DropdownItem, Stack } from '@optimus-web/core';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { compactArray } from '@mews/fp';
import { Reservation } from '@mews-commander/data-access';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { ResourceStateTag } from '../../../../components';
import {
    setInspected as setInspectedAction,
    skipTodayCleaning as skipTodayCleaningAction,
    setReservationsSkipCleaning as setReservationsSkipCleaningAction,
    setDirty,
} from '../../actions';
import { withActiveWindowLoader } from '../../../../core';
import { getReservationHousekeepingFlagSelector } from './typedSelectors';

const ReservationHousekeepingStatus = ({ assignedResourceId, id }: Reservation) => {
    const dispatch = useCommanderThunkDispatch();

    const setInspected = async () =>
        withActiveWindowLoader(async () => {
            await dispatch(setInspectedAction([assignedResourceId]));
            showSuccessAlert({ description: lt('MakeSpaceInspectedSuccessMessage') });
        });

    const setReservationsSkipCleaning = async (newSkipCleaning: boolean) =>
        withActiveWindowLoader(async () => {
            await dispatch(setReservationsSkipCleaningAction([id], newSkipCleaning));
            if (!newSkipCleaning && assignedResourceId) {
                await dispatch(setDirty([assignedResourceId]));
            }
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });

    const skipTodayCleaning = async () =>
        withActiveWindowLoader(async () => {
            await dispatch(skipTodayCleaningAction([assignedResourceId as string]));
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        });

    const flags = useCommanderSelector((state) =>
        getReservationHousekeepingFlagSelector(state, { id, resourceId: assignedResourceId })
    );

    const menuItemProps = compactArray([
        flags?.makeSpaceInspected
            ? {
                  label: <LocalizedText textKey={TextKey.MakeSpaceInspected} />,
                  onClick: setInspected,
                  'data-test-make-inspected': true,
              }
            : null,
        flags?.skipTodayCleaning
            ? {
                  label: <LocalizedText textKey={TextKey.SkipTodayCleaning} />,
                  onClick: skipTodayCleaning,
                  'data-test-skip-next-cleaning': true,
              }
            : null,
        flags?.skipAllCleaning
            ? {
                  label: <LocalizedText textKey={TextKey.SkipAllCleaning} />,
                  onClick: async () => setReservationsSkipCleaning(true),
                  'data-test-set-skip-cleaning': 'enable',
              }
            : null,
        flags?.enableCleaning
            ? {
                  label: <LocalizedText textKey={TextKey.EnableCleaning} />,
                  onClick: async () => setReservationsSkipCleaning(false),
                  'data-test-set-skip-cleaning': 'disable',
              }
            : null,
    ]);

    return (
        <Stack spacing="100" itemAlignment="center">
            {assignedResourceId !== null ? <ResourceStateTag resourceId={assignedResourceId} /> : null}

            {menuItemProps.length !== 0 ? (
                <DropDownButton icon={IconName.More} size="small" variant="ghost">
                    {menuItemProps.map((props) => (
                        <DropdownItem {...props} />
                    ))}
                </DropDownButton>
            ) : null}
        </Stack>
    );
};

export default ReservationHousekeepingStatus;
